/**
 * localStorage keys used to persist LedgerSync connect state across the
 * redirect to the hosted Connect widget and back (DCP-216). The widget is
 * redirect-based and returns no params we control, so the callback page reads
 * these to know which pending connection to poll and where to return.
 *
 * NO FACILITY KEY: a connection is an ORGANIZATION-level object since DCP-324
 * — the connect flow has no facility to record, the facility is chosen per
 * ACCOUNT after the bank is linked, and `setLedgerSyncOAuthState`'s only
 * caller (`useCreateLedgerSyncWidget`) never supplied one even while the key
 * still existed. `returnPath` carries where the callback should return to.
 */
export const LEDGERSYNC_CONNECTION_KEY = 'ledgersync_connection_id'
export const LEDGERSYNC_RETURN_KEY = 'ledgersync_return_path'

/**
 * Persist the pending-connect state before redirecting to the widget.
 * Wrapped in try/catch for private-mode / storage-blocked browser resilience:
 * localStorage.setItem throws a DOMException when storage is unavailable.
 */
export function setLedgerSyncOAuthState(state: { connectionId: string; returnPath: string }): void {
  try {
    localStorage.setItem(LEDGERSYNC_CONNECTION_KEY, state.connectionId)
    localStorage.setItem(LEDGERSYNC_RETURN_KEY, state.returnPath)
  } catch (e) {
    console.warn(
      '[ledgersync] localStorage unavailable — connect state will not survive the redirect',
      e,
    )
  }
}

/**
 * Read the persisted connect state on the callback page.
 * Returns all-null on storage failure (the callback page handles the all-null
 * case gracefully, showing an error and letting the user retry).
 * Wrapped in try/catch for private-mode / storage-blocked browser resilience.
 */
export function getLedgerSyncOAuthState(): {
  connectionId: string | null
  returnPath: string | null
} {
  try {
    return {
      connectionId: localStorage.getItem(LEDGERSYNC_CONNECTION_KEY),
      returnPath: localStorage.getItem(LEDGERSYNC_RETURN_KEY),
    }
  } catch {
    return { connectionId: null, returnPath: null }
  }
}

/**
 * Clear the connect state only while it still belongs to `connectionId`, so
 * a settled poll for one connection never wipes a newer flow's state. A no-op
 * once the callback page has already cleared it.
 *
 * @param connectionId - tenant `ledgersync_connections.id` whose flow is over
 */
export function clearLedgerSyncOAuthStateFor(connectionId: string): void {
  if (getLedgerSyncOAuthState().connectionId === connectionId) clearLedgerSyncOAuthState()
}

/**
 * Remove all LedgerSync connect state from localStorage.
 * Wrapped in try/catch for private-mode / storage-blocked browser resilience;
 * failure to clear is non-fatal as the keys simply expire on their own.
 */
export function clearLedgerSyncOAuthState(): void {
  try {
    localStorage.removeItem(LEDGERSYNC_CONNECTION_KEY)
    localStorage.removeItem(LEDGERSYNC_RETURN_KEY)
  } catch {
    // Storage unavailable — nothing to clear, silently ignore.
  }
}
